import React, { useEffect, useState } from "react";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Select, MenuItem, Tooltip, DialogContent, DialogTitle, Dialog, DialogActions, TextField } from "@mui/material";
import { toast } from "react-toastify";
import Loader from "../../../components/common/Loader";
import { ApplyLeaveRequest, fetch2factorUsers, fetchEntityById,} from "../../../apis/apiService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchBar from "../../../components/SearchBar"; // ✅ adjust import path
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Typography } from "@mui/material";
import { IconButton } from "@mui/material";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../ThemeContext";

function Twofactor() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState(3); // 3 = All, 2 = Enable, 1 = Disable
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [recoverdata, setRecoverData] = useState(null);
  const [twofaopen, setTwofaOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const passwordRef = React.useRef(null);

  const token = localStorage.getItem("access_token");
  const { id: loggedInUserId } = useCurrentUser();
  const navigate = useNavigate();

  const handleOpenRecoveryModal = async (row) => {
    setLoading(true);

    try {
      const response = await fetchEntityById("get2FARecoveryCodes", row.user_id);

      if (response?.status && response?.data) {
        setRecoverData(response.data);
        setOpen(true); // ✅ Open modal only after successful API call
      } 
      else {
        toast.error(response?.message || "Failed to fetch recovery codes");
      }
    } 
    catch (err) {
      console.error("Error fetching recovery codes:", err);
      toast.error(
        err?.data?.message || "Something went wrong while fetching codes"
      );
    } 
    finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setRecoverData(null);
  };

  const handleCopyCodes = () => {
    if (!recoverdata?.two_fa_recovery_codes?.length) return;

    // Create a string of all codes separated by newline
    const codesString = recoverdata.two_fa_recovery_codes
      .map((item) => item.code)
      .join("\n");

    // Copy to clipboard
    navigator.clipboard
      .writeText(codesString)
      .then(() => {
        toast.success("Recovery codes copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy codes:", err);
        toast.error("Failed to copy codes");
      });
  };

  const generateTxt = (data) => {
    const today = new Date();
    const dateString = today.toLocaleDateString();

    let txtContent = `Name: ${data.name || ""}\n`;
    txtContent += `Download Date: ${dateString}\n\n`;
    txtContent += "#\tCode\t\tUsed\n";

    data.two_fa_recovery_codes.forEach((codeObj, idx) => {
      txtContent += `${idx + 1}\t${codeObj.code}\t${
        codeObj.used ? "Yes" : "No"
      }\n`;
    });

    const blob = new Blob([txtContent], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `2FA_Recovery_Codes_${data.name || "User"}.txt`);
  };

  const handleOpen2faRecoveryModal = (row) => {
    setSelectedUser(row);
    setTwofaOpen(true);
  };

  const handle2faClose = () => {
    setTwofaOpen(false);
  };

  const handleToggleClose = () => {
    setToggleOpen(false);
    setPassword("");
  };

  const handle2faConfirm = async (passwordInput = null) => {
    if (!selectedUser) return;

    try {
      setLoading(true);

      const payload = {
        user_id: selectedUser.user_id,
        password: passwordInput || null,
      };

      const response = await ApplyLeaveRequest("reset-2fa", payload, token);

      if (response?.success) {
        toast.success("2FA Disabled Successfully");
        fetch2factorUserList(filter);
        handle2faClose();
        handleToggleClose();

        if (passwordInput) {
          navigate("/twofactor-scan");
        }
      } 
      else {
        toast.error(response?.message || "Failed to disable 2FA");
      }
    } catch (error) {
      console.error("API error:", error);

      if (error.response?.status === 401) {
        toast.error("Incorrect password. Please try again.");
        return;
      }

      toast.error(error?.message || "Something went wrong");
    } 
    finally {
      setLoading(false);
    }
  };

  // ✅ API call with backend filter mapping
  const fetch2factorUserList = async (filterValue = 3) => {
    setLoading(true);
    try {
      let url = "2faUsersList";

      if (filterValue === 1) {
        url += "?view_type=1"; // disable
      } else if (filterValue === 2) {
        url += "?view_type=2"; // enable
      }
      // if 3 → All → no query param

      const response = await fetch2factorUsers(url, token);

      if (response.status) {
        setUsers(response.data.map((user) => ({ ...user, id: user.user_id })));
      } 
      else {
        setUsers([]);
      }
    } 
    catch (error) {
      toast.error(error.message);
    } 
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch2factorUserList(filter);
  }, []);

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setFilter(newFilter);
    fetch2factorUserList(newFilter);
  };

  // const handleSearchChange = (value) => {
  //     setSearchTerm(value);
  // };

  const handleSearchChange = (valueOrEvent) => {
    // Handle both direct string and event object
    const value =
      typeof valueOrEvent === "string"
        ? valueOrEvent
        : valueOrEvent?.target?.value ?? "";

    setSearchTerm(value);
  };

  // Example counts
  const totalCount = users.length;
  const activeCount = users.filter((u) => u.is_two_fa_verified).length;
  const inactiveCount = totalCount - activeCount;

  const columns = [
    { field: "name", headerName: "User Name", width: 200 },
    { field: "email", headerName: "Email", width: 200 },
    {
      field: "is_two_fa_verified",
      headerName: "Two Factor",
      width: 150,
      renderCell: (params) =>
        params.value ? (
          <span style={{ color: "green" }}>Enabled </span>
        ) : (
          <span style={{ color: "red" }}>Disabled </span>
        ),
    },
    { field: "two_fa_secret", headerName: "Secret Code", width: 250 },
    {
      field: "two_fa_recovery_codes",
      headerName: "Recovery Codes",
      width: 250,
      renderCell: (params) => {
        const recoveryCodes = params.row.two_fa_recovery_codes;
        const hasRecoveryCodes =
          Array.isArray(recoveryCodes) && recoveryCodes.length > 0;

        if (!hasRecoveryCodes) {
          return <span style={{ color: "#888" }}>—</span>;
        }

        return (
          <button
            className="btn btn-sm add_btn"
            onClick={() => handleOpenRecoveryModal(params.row)} // ✅ calls the function below
            style={{
              background: "rgba(18, 183, 159, 1)",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "4px 8px",
              cursor: "pointer",
            }}
          >
            View Recovery
          </button>
        );
      },
    },
    {
      field: "reset_2fa",
      headerName: "Reset 2FA",
      width: 250,
      renderCell: (params) => {
        const { two_fa_secret } = params.row;

        if (!two_fa_secret) {
          return <span style={{ color: "#888" }}>—</span>;
        }

        return (
          <button
            className="btn btn-sm cancel_btn"
            onClick={() => {
              if (params.row.user_id == loggedInUserId) {
                // 🔐 Logged-in user → open password modal
                setSelectedUser(params.row);
                setToggleOpen(true);
              } else {
                // 👤 Other user → open normal Reset modal
                handleOpen2faRecoveryModal(params.row);
              }
            }}
            style={{
              backgroundColor: "rgba(255, 0, 0, 1)",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "4px 8px",
              cursor: "pointer",
            }}
          >
            Reset 2FA
          </button>
        );
      },
    },

    {
      field: "two_fa_remember_until",
      headerName: "2FA Remember untill",
      width: 200,
      align: "center",
    },
  ];

  // ✅ frontend search
  const filteredUsers = users.filter((u) => {
    const term = (searchTerm || "").toString().toLowerCase();
    return (
      u.name?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="py-3">
      <Dialog
        open={twofaopen}
        onClose={handle2faClose}
        TransitionProps={{
          onExited: () => setSelectedUser(null), // Runs AFTER closing animation
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Reset 2FA
          <IconButton
            aria-label="close"
            onClick={handle2faClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <p>
            Do you want to reset 2FA for{" "}
            <strong>{selectedUser?.name || selectedUser?.email}</strong>?
          </p>
        </DialogContent>

        <DialogActions>
          <button onClick={handle2faClose} className="cancel_btn">
            No
          </button>
          <button
            onClick={() => handle2faConfirm()}
            className="add_btn"
            variant="contained"
          >
            Yes
          </button>
        </DialogActions>
      </Dialog>
      
      <Modal open={toggleOpen} onClose={handleToggleClose}>
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "var(--cell-bg-color, white)",
            color: "var(--text-color, black)",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            textAlign: "center",
            minWidth: 350,
          }}
        >
          <h3>Please Enter Current Password</h3>

          <TextField
            inputRef={passwordRef}
            type="password"
            variant="outlined"
            placeholder="Enter your password"
            fullWidth
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handle2faConfirm(password);
              }
            }}
            error={passwordError}
            helperText={passwordError ? "Password is required" : ""}
            sx={{ mt: 2, mb: 3 }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <button onClick={handleToggleClose} className="cancel_btn">
              Cancel
            </button>

            <button
              type="button"
              className="add_btn"
              disabled={loading}
              onClick={() => {
                if (!password) {
                  setPasswordError(true);
                  return;
                }
                handle2faConfirm(password);
              }}
            >
              {loading ? "Submitting..." : "Yes"}
            </button>
          </Box>
        </Box>
      </Modal>

      <Box
        className="d-flex justify-content-between align-items-center"
        sx={{
          border: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "16px",
          backgroundColor: isDarkMode ? "transparent" : "#fff",
        }}
      >
        {/* Filter + counts */}
        <div className="d-flex gap-2 align-items-center">
          <Select value={filter} onChange={handleFilterChange}>
            <MenuItem value={3}>All</MenuItem>
            <MenuItem value={2}>Enable</MenuItem>
            <MenuItem value={1}>Disable</MenuItem>
          </Select>
          <Tooltip arrow
            title={
              <>
                <div>
                  <span style={{ fontSize: 12 }}>📊</span> Total Users:{" "}
                  {totalCount}
                </div>
                <div>
                  <span style={{ fontSize: 12 }}>🟢</span> Active Users:{" "}
                  {activeCount}
                </div>
                <div>
                  <span style={{ fontSize: 12 }}>🔴</span> Inactive Users:{" "}
                  {inactiveCount}
                </div>
              </>
            }
          >
            <IconButton
              sx={{
                padding: "8px",
                "&:hover": {
                  backgroundColor: "var(--icon-hover-bg)",
                },
              }}
            >
              <InfoOutlinedIcon style={{ cursor: "pointer" }} color="error" />
            </IconButton>
          </Tooltip>
        </div>

        {/* Search + actions */}
        <div className="d-flex align-items-center">
          <SearchBar
            placeholder="Search users"
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
          />
        </div>
      </Box>

      {/* Data grid */}
      <Box style={{ width: "100%" }}>
        {loading ? (
          <Loader />
        ) : (
          <Box sx={{ height: filteredUsers.length === 0 ? 300 : "100%" }}>
            <DataGrid
              rows={filteredUsers}
              columns={columns}
              loading={loading}
              pagination
              pageSizeOptions={[25, 50, 75, 100]}
              disableSelectionOnClick
            />
          </Box>
        )}
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "var(--cell-bg-color, #fff)",
            color: "var(--text-color, #000)",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            textAlign: "center",
            minWidth: 350,
            outline: "none",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "var(--text-color, black)",
            }}
          >
            <CloseIcon />
          </IconButton>

          {loading ? (
            <Loader />
          ) : recoverdata && recoverdata.two_fa_recovery_codes?.length > 0 ? (
            <>
              <Typography variant="h5" gutterBottom>
                {recoverdata.name}
              </Typography>
              <table className="modal-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Used</th>
                  </tr>
                </thead>
                <tbody>
                  {recoverdata.two_fa_recovery_codes.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.code}</td>
                      <td>{item.used ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button
                className="add_btn me-3 cursor-pointer"
                onClick={handleCopyCodes}
              >
                Copy
              </button>

              <button
                className="add_btn me-3"
                onClick={() => generateTxt(recoverdata)}
              >
                Download Codes
              </button>
            </>
          ) : (
            <Typography variant="body1" sx={{ color: "var(--text-color)" }}>
              No data found.
            </Typography>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default Twofactor;
